import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Auto-reload on chunk load failure (happens after deploy with new hashes)
    if (error.message?.includes('dynamically imported module') || error.message?.includes('Failed to fetch')) {
      const lastReload = sessionStorage.getItem('chunk-reload');
      const now = Date.now();
      // Only auto-reload once per 30 seconds to avoid infinite loops
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem('chunk-reload', String(now));
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-8">
          <div className="text-center max-w-md">
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Orbitron', color: 'var(--theme-primary)' }}>
              Oops! Something went wrong
            </h1>
            <p className="text-gray-400 mb-6">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="px-6 py-3 rounded-lg font-bold transition-all"
                style={{ background: 'var(--theme-primary)', color: 'white' }}
              >
                Try again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 rounded-lg font-bold border border-gray-600 hover:border-gray-400 transition-all"
              >
                Return home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
